import React, { useMemo, useState } from 'react';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isToday,
  parseISO,
  startOfMonth,
  startOfWeek,
  subMonths
} from 'date-fns';
import SafeIcon from '../../common/SafeIcon';
import SellerCalendarOrder from './SellerCalendarOrder';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiArrowRight, FiCalendar } = FiIcons;
const STATUSES = [
  'all',
  'pending',
  'accepted',
  'preparing',
  'ready',
  'completed',
  'declined',
  'cancelled'
];
const DEFAULT_STATUSES = ['pending', 'accepted', 'preparing', 'ready'];

const SellerOrdersCalendar = ({ orders, onSelectOrder }) => {
  const [month, setMonth] = useState(() => new Date());
  const [status, setStatus] = useState(DEFAULT_STATUSES);
  const days = useMemo(() => {
    const first = startOfWeek(startOfMonth(month));
    const last = endOfWeek(endOfMonth(month));
    return eachDayOfInterval({ start: first, end: last });
  }, [month]);

  const scheduledOrders = useMemo(() => orders
    .filter((order) => order.requested_date)
    .filter((order) => status.includes('all') || status.includes(order.status))
    .slice()
    .sort((first, second) => String(first.requested_time || '')
      .localeCompare(String(second.requested_time || ''))), [orders, status]);

  const ordersForDay = (day) => scheduledOrders.filter((order) => {
    const requestedDate = parseISO(order.requested_date);
    return !Number.isNaN(requestedDate.getTime()) && isSameDay(requestedDate, day);
  });

  const toggleStatus = (selectedStatus) => {
    setStatus((current) => {
      if (selectedStatus === 'all') {
        return current.includes('all') ? [] : ['all'];
      }

      const statuses = current.filter((item) => item !== 'all');
      return statuses.includes(selectedStatus)
        ? statuses.filter((item) => item !== selectedStatus)
        : [...statuses, selectedStatus];
    });
  };

  return (
    <section aria-label="Orders calendar" className="space-y-5">
      <div className="flex flex-col justify-between gap-4 rounded-2xl border border-slate-100 bg-slate-50 p-5 sm:flex-row sm:items-center">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-xl bg-indigo-100 text-indigo-600">
            <SafeIcon icon={FiCalendar} className="text-xl" />
          </span>
          <div>
            <h2 className="text-lg font-black text-slate-900">
              Fulfillment calendar
            </h2>
            <p className="text-sm font-medium text-slate-500">
              Orders shown by requested date and time
            </p>
          </div>
        </div>
        <label className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-2.5">
          <span className="text-xs font-black uppercase tracking-wider text-slate-500">
            Status
          </span>
          <span className="flex flex-wrap gap-x-3 gap-y-2">
            {STATUSES.map((option) => (
              <label key={option} className="flex items-center gap-1.5 text-sm font-bold capitalize text-slate-800">
                <input
                  type="checkbox"
                  checked={status.includes(option)}
                  onChange={() => toggleStatus(option)}
                  className="h-4 w-4 rounded border-slate-300 text-indigo-600 outline-none focus:ring-indigo-500"
                />
                {option === 'all' ? 'All statuses' : option}
              </label>
            ))}
          </span>
        </label>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => setMonth((current) => subMonths(current, 1))}
            aria-label="Previous month"
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
          >
            <SafeIcon icon={FiArrowLeft} />
          </button>
          <button
            type="button"
            onClick={() => setMonth(new Date())}
            className="rounded-xl border border-slate-200 bg-white px-4 py-2.5 text-xs font-black uppercase tracking-wider text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
          >
            Today
          </button>
          <button
            type="button"
            onClick={() => setMonth((current) => addMonths(current, 1))}
            aria-label="Next month"
            className="flex h-11 w-11 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-600 transition hover:border-indigo-200 hover:text-indigo-600"
          >
            <SafeIcon icon={FiArrowRight} />
          </button>
        </div>
        <h3 className="text-xl font-black text-slate-900">
          {format(month, 'MMMM yyyy')}
        </h3>
      </div>

      <div className="overflow-x-auto rounded-2xl border border-slate-100">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-7 border-b border-slate-100 bg-slate-50">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day) => (
              <div
                key={day}
                className="px-3 py-3 text-center text-[10px] font-black uppercase tracking-widest text-slate-400"
              >
                {day}
              </div>
            ))}
          </div>
          <div className="grid grid-cols-7">
            {days.map((day) => {
              const dayOrders = ordersForDay(day);
              return (
                <div
                  key={format(day, 'yyyy-MM-dd')}
                  className={`min-h-36 border-b border-r border-slate-100 p-2 ${
                    isSameMonth(day, month) ? 'bg-white' : 'bg-slate-50/70'
                  }`}
                >
                  <div className="mb-2 flex justify-end">
                    <span className={`flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold ${
                      isToday(day)
                        ? 'bg-indigo-600 text-white'
                        : isSameMonth(day, month)
                          ? 'text-slate-700'
                          : 'text-slate-300'
                    }`}>
                      {format(day, 'd')}
                    </span>
                  </div>
                  <div className="space-y-1.5">
                    {dayOrders.map((order) => (
                      <SellerCalendarOrder
                        key={order.id}
                        order={order}
                        onSelect={onSelectOrder}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      {scheduledOrders.length === 0 && (
        <p className="rounded-xl border border-dashed border-slate-200 px-4 py-6 text-center text-sm font-medium text-slate-500">
          No orders with requested dates match this status filter.
        </p>
      )}
    </section>
  );
};

export default SellerOrdersCalendar;
